import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { SocialLinks } from '@/components/layout/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [donateUrl, setDonateUrl] = useState('/contact');
  const [showShop, setShowShop] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [siteRes, storeRes, schedRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/settings`),
          fetch(`${BACKEND_URL}/api/store/settings`),
          fetch(`${BACKEND_URL}/api/schedule/settings`),
        ]);
        if (siteRes.ok) {
          const data = await siteRes.json();
          if (data.zeffy_link) setDonateUrl(data.zeffy_link);
        }
        if (storeRes.ok) {
          const storeData = await storeRes.json();
          setShowShop(storeData.show_shop_link !== false);
        }
        if (schedRes.ok) {
          const schedData = await schedRes.json();
          setShowSchedule(schedData.enabled === true);
        }
      } catch {}
    };
    fetchSettings();
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Events', href: '/events' },
    { name: 'Elevated Escape', href: '/elevated-escape/tickets' },
    ...(showSchedule ? [{ name: 'Schedule', href: '/schedule' }] : []),
    { name: 'Calendar', href: '/calendar' },
    { name: 'Programs', href: '/programs' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Our People', href: '/our-people' },
    { name: 'Newsletter', href: '/newsletter' },
    ...(showShop ? [{ name: 'Shop', href: '/shop' }] : []),
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header className="bg-brand-dark">
      {/* Logo Banner */}
      <div className="w-full flex justify-center py-4 sm:py-6">
        <Link to="/">
          <img
            src="/images/site/logo.png"
            alt="ElevateMN - Mental Health & Community Support"
            className="h-20 sm:h-28 lg:h-32 w-auto object-contain"
          />
        </Link>
      </div>
      
      {/* Navigation */}
      <nav className="mx-auto max-w-7xl px-6 lg:px-8" aria-label="Top">
        <div className="flex w-full items-center justify-between py-4">

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:gap-x-12">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? 'text-brand-gold'
                    : 'text-white hover:text-brand-gold'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Donate CTA */}
          <div className="hidden lg:flex">
            <a
              href={donateUrl}
              target={donateUrl.startsWith('http') ? '_blank' : '_self'}
              rel={donateUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="bg-brand-gold px-5 py-2.5 text-sm font-semibold text-brand-dark transition-opacity hover:opacity-90"
              data-testid="nav-donate-btn"
            >
              Donate
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <button
              type="button"
              className="text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">Open menu</span>
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-6">
            <div className="flex flex-col space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-base font-medium ${
                    isActive(item.href)
                      ? 'text-brand-gold'
                      : 'text-white'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <a
                href={donateUrl}
                target={donateUrl.startsWith('http') ? '_blank' : '_self'}
                rel={donateUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="inline-block bg-brand-gold px-5 py-2.5 text-sm font-semibold text-brand-dark text-center"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-donate-btn"
              >
                Donate
              </a>
              <div className="pt-4 border-t border-gray-700">
                <SocialLinks iconSize="w-6 h-6" />
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
